import { type Validation } from '@wix/ambassador-forms-v4-form/types';
import {
  type InputFieldType,
  type ReadonlyFieldType,
} from '../../../constants';
import { type PossibleValues } from './form-values';
import { type RichContent } from '@wix/ricos';
import { type CustomErrors } from '../../../field-error';

export interface FormView {
  parentId?: string;
  name: string;
  grid: Grid;
  fields: Field[];
}

export interface Grid {
  columns: number;
  rowHeight: number;
  margin: Margin;
  padding: Margin;
}

export interface Margin {
  horizontal: number;
  vertical: number;
}

export interface Properties<
  ValueType extends PossibleValues = PossibleValues,
  LabelType extends string | RichContent = string,
> {
  label?: LabelType;
  disabled: boolean;
  readOnly: boolean;
  hidden: boolean;
  required: boolean;
  defaultValue: ValueType | undefined;
  pii: boolean;
  validation?: Validation;
  nestedFormId?: string | null;
  form?: FormView;
  customErrors?: CustomErrors;
}

export interface Layout {
  row: number;
  column: number;
  width: number;
  height: number;
  static?: boolean;
  minW?: number;
}

export interface LayoutByBreakpoint {
  large: Layout | undefined;
  small: Layout | undefined;
}

export type Field = ReadOnlyField | InputField | SubmitButtonField;

interface BaseField {
  id: string;
  properties: Properties;
  layout: Layout;
  layoutByBreakpoint: LayoutByBreakpoint;
}

export interface SubmitButtonField extends BaseField {
  fieldType: 'SUBMIT_BUTTON';
}

export interface ReadOnlyField extends BaseField {
  fieldType: ReadonlyFieldType;
}

export interface InputField extends BaseField {
  fieldType: InputFieldType;
  target: string;
  path: string;
}

export interface NestedFormField extends BaseField {
  fieldType: InputFieldType;
  target: string;
  path: string;
}

export function isInputField(field: Field): field is InputField {
  return Boolean((field as InputField).target);
}

export function isNestedFormField(field: Field): field is NestedFormField {
  const nestedFormField = field as NestedFormField;
  return Boolean(
    nestedFormField.target && nestedFormField.properties.nestedFormId,
  );
}
